import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { adminApi, CostReport, ProfitabilityReport } from '../services/api';
import PageHelp from '../components/PageHelp';
import { PAGE_HELP } from '../components/page-help-content';
import FxRateBadge from '../components/FxRateBadge';
import PeriodSelector from '../components/PeriodSelector';
import { useUsdZarRate } from '../hooks/useUsdZarRate';
import { getCurrentPeriod, getRecentPeriods, formatPeriodLabel } from '../utils/period';
import { formatUsd, formatZar } from '../utils/format';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { IS_BROKER_ADMIN } from '../config/admin-build';

/** Line colours for the rolling-margin chart (bronze / silver / gold). */
const TIER_LINE_COLORS: Record<string, string> = {
  Bronze: '#b7791f',
  Silver: '#718096',
  Gold: '#d69e2e',
};

const STACK_LABELS: Record<'intelligent-automation' | 'broker-ai', string> = {
  'intelligent-automation': 'Intelligent Automation',
  'broker-ai': 'Broker AI',
};

const CostReportPage: React.FC = () => {
  const [period, setPeriod] = useState<string>(getCurrentPeriod());
  const [report, setReport] = useState<CostReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const periods = useMemo(() => getRecentPeriods(), []);
  const fx = useUsdZarRate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getCostReport({ period })
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          setReport(response.data);
        } else {
          setError('Failed to load cost report.');
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load cost report.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const adminStack = report?.adminStack;
  const stackTotal = adminStack ? report?.byStack?.[adminStack] : undefined;
  const titanShare = report?.titanShareUsd ?? 0;
  const showTitanShare = adminStack === 'broker-ai' && (stackTotal?.totalCostUsd ?? 0) > 0;

  // --- Profitability: server-computed margins + rolling trend ---------------
  // ALL the math (tier prices, USD->ZAR, margin, trend) is done by the backend
  // (GET /admin/stats/profitability); this component just renders it. Fetched
  // once, on the broker-admin build only.
  const [profitability, setProfitability] = useState<ProfitabilityReport | null>(null);

  useEffect(() => {
    if (!IS_BROKER_ADMIN) return;
    let cancelled = false;
    adminApi
      .getProfitability({ months: 6 })
      .then((r) => {
        if (!cancelled && r.success) setProfitability(r.data);
      })
      .catch(() => {
        /* leave null — the section just won't render */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Defence in depth: gate on the broker-admin BUILD flag (per
  // broker-admin-visibility-on-new-surface) AND the endpoint's own broker-stack
  // gate (it returns available:false for a non-broker admin).
  //
  // Access model: the broker-AI admin (admin.broker-ai.org) Cognito Admins group is
  // internal staff only (NOT broker partners), confirmed with the product owner, so
  // margin is only ever seen by internal users. If broker partners are ever granted
  // broker-admin credentials, add a per-user role gate (here + on the endpoint).
  const showProfitability =
    IS_BROKER_ADMIN && !!profitability?.available && (profitability?.current?.completed ?? 0) > 0;

  // Recharts rows: { label, Bronze, Silver, Gold } straight from the backend trend.
  const trendData = useMemo(
    () => (profitability?.trend ?? []).map((p) => ({ label: p.label, ...p.marginByTier })),
    [profitability],
  );
  const trendTierNames = profitability?.trendTierNames ?? ['Bronze', 'Silver', 'Gold'];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Cost Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Per-stack AI spend for the selected month. Cost figures are calculated from per-job
            token usage and may differ slightly from the provider invoice.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
            data-testid="period-hint"
          >
            Showing {formatPeriodLabel(report?.period ?? period)}. Use the Period selector (top
            right) to see other months. Cost data is retained for about 90 days.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <FxRateBadge
            rate={fx.rate}
            loading={fx.loading}
            error={fx.error}
            fetchedAt={fx.fetchedAt}
            onRefresh={fx.refetch}
          />
          <PeriodSelector period={period} periods={periods} onChange={setPeriod} />
        </Stack>
      </Stack>

      <PageHelp {...PAGE_HELP.costReport} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && report && (
        <>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            data-testid="summary-cards"
          >
            <Card sx={{ flex: 1 }} data-testid="card-stack-total">
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {STACK_LABELS[adminStack ?? 'intelligent-automation']} — {formatPeriodLabel(report.period)}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>
                  {formatUsd(stackTotal?.totalCostUsd ?? 0)}
                </Typography>
                {fx.rate != null && (
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }} data-testid="card-stack-total-zar">
                    {formatZar((stackTotal?.totalCostUsd ?? 0) * fx.rate)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {(stackTotal?.completed ?? 0).toLocaleString()} completed jobs ·{' '}
                  {(stackTotal?.failed ?? 0).toLocaleString()} failed
                </Typography>
              </CardContent>
            </Card>

            {showTitanShare && (
              <Card
                sx={{ flex: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}
                data-testid="card-titan-share"
              >
                <CardContent>
                  <Typography variant="overline" sx={{ opacity: 0.85 }}>
                    Titan share (50% of Broker AI)
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>
                    {formatUsd(titanShare)}
                  </Typography>
                  {fx.rate != null && (
                    <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9 }} data-testid="card-titan-share-zar">
                      {formatZar(titanShare * fx.rate)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                    Settle at month-end against the Anthropic + OpenAI workspace invoices
                    (Phase B) or this internal calculation in the meantime.
                  </Typography>
                </CardContent>
              </Card>
            )}

            <Card sx={{ flex: 1 }} data-testid="card-meta">
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Coverage
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {report.eligibleOrgsForStack.toLocaleString()} eligible org(s) on this stack
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {report.byOrg.length.toLocaleString()} org(s) had activity this period
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Generated {new Date(report.generatedAt).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Stack>

          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" data-testid="org-table">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Cost (USD)</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }} data-testid="col-zar-header">
                      Cost (ZAR)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Completed</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Failed</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byOrg.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No org activity for this period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {report.byOrg.map((row) => (
                    <TableRow key={row.organizationId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.organizationName || row.organizationId}
                        </Typography>
                        {row.organizationName && (
                          <Typography variant="caption" color="text.secondary">
                            {row.organizationId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatUsd(row.totalCostUsd)}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 600 }} data-testid={`zar-${row.organizationId}`}>
                        {fx.rate != null ? formatZar(row.totalCostUsd * fx.rate) : '—'}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        {row.completed.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        {row.failed.toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        {row.submitted.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {showProfitability && profitability && (
            <Box sx={{ mt: 4 }} data-testid="profitability-section">
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Profitability
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What we charge per run at each plan vs. the AI cost per run — AI-cost margin
                only (infrastructure excluded). 1 run = 1 credit, so the per-run charge is the
                plan's per-credit price. Computed server-side.
              </Typography>

              <Card sx={{ mb: 3 }} data-testid="avg-cost-card">
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Avg AI cost / run — {formatPeriodLabel(profitability.period ?? period)}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mt: 0.5 }}
                    data-testid="avg-cost-per-run"
                  >
                    {profitability.current?.avgCostZar != null
                      ? formatZar(profitability.current.avgCostZar)
                      : '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {(profitability.current?.completed ?? 0).toLocaleString()} completed runs
                    {profitability.current?.avgCostZar != null
                      ? ` · ${formatUsd(profitability.current.avgCostUsd)} / run`
                      : ' · FX rate unavailable'}
                    {profitability.usdZarRate != null
                      ? ` · at R${profitability.usdZarRate.toFixed(4)}/$`
                      : ''}
                  </Typography>
                </CardContent>
              </Card>

              <Paper variant="outlined" sx={{ mb: 3 }}>
                <TableContainer>
                  <Table size="small" data-testid="margin-table">
                    <TableHead sx={{ bgcolor: 'action.hover' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Charge / run</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Cost / run</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Profit / run</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Margin</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>FspHub charge</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>FspHub margin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(profitability.tiers ?? []).map((tier) => (
                        <TableRow key={tier.name} hover data-testid={`margin-row-${tier.name}`}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {tier.name}
                              {tier.proposed && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 0.5 }}
                                >
                                  (proposed)
                                </Typography>
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{formatZar(tier.chargeZar)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            {tier.costZar != null ? formatZar(tier.costZar) : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 600 }}>
                            {tier.profitZar != null ? formatZar(tier.profitZar) : '—'}
                          </TableCell>
                          <TableCell
                            sx={{ textAlign: 'right', fontWeight: 600 }}
                            data-testid={`margin-pct-${tier.name}`}
                          >
                            {tier.marginPct != null ? `${(tier.marginPct * 100).toFixed(1)}%` : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{formatZar(tier.fspChargeZar)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>
                            {tier.fspMarginPct != null ? `${(tier.fspMarginPct * 100).toFixed(1)}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Rolling margin — last 6 months
              </Typography>
              {trendData.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ height: 300 }} data-testid="margin-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          // Auto-zoom to the data band. Margins cluster high (~90-96%),
                          // so a fixed 50-100% axis crushes the lines against the top and
                          // hides month-to-month movement. Pad ~2pp each side, snap to whole
                          // percents, and clamp to [0, 100%].
                          domain={[
                            (dataMin: number) => Math.max(0, Math.floor((dataMin - 0.02) * 100) / 100),
                            (dataMax: number) => Math.min(1, Math.ceil((dataMax + 0.02) * 100) / 100),
                          ]}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v: number) => `${(v * 100).toFixed(1)}%`}
                        />
                        <RechartsTooltip formatter={(v: number) => `${(v * 100).toFixed(1)}%`} />
                        <Legend />
                        {trendTierNames.map((name) => (
                          <Line
                            key={name}
                            type="monotone"
                            dataKey={name}
                            stroke={TIER_LINE_COLORS[name] ?? '#3182ce'}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  data-testid="margin-trend-empty"
                >
                  {profitability.usdZarRate == null
                    ? 'Rolling margin needs the USD→ZAR rate (the FX provider was unavailable).'
                    : 'Not enough recent months with runs to plot a trend yet.'}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CostReportPage;
